import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';

export interface UiState {
  queueCollapsed: boolean;
  devicesCollapsed: boolean;
  detailsCollapsed: boolean;
  libraryCollapsed: boolean;
  showLyrics: boolean;
  loginTooltipOpen: boolean;
  loginButtonOpen: boolean;
  loginModalItem: string | null;
  fullscreenNowPlaying: boolean;
  miniplayerOpen: boolean;
}

const initialState: UiState = {
  queueCollapsed: true,
  devicesCollapsed: true,
  detailsCollapsed: true,
  libraryCollapsed: true,
  showLyrics: false,
  loginTooltipOpen: false,
  loginButtonOpen: false,
  loginModalItem: null,
  fullscreenNowPlaying: false,
  miniplayerOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openLoginButton(state) {
      state.loginButtonOpen = true;
      state.loginModalItem = null;
      state.loginTooltipOpen = false;
    },
    closeLoginButton(state) {
      state.loginButtonOpen = false;
    },
    openLoginModal(state, action: PayloadAction<string>) {
      state.loginModalItem = action.payload;
      state.loginButtonOpen = false;
      state.loginTooltipOpen = false;
    },
    closeLoginModal(state) {
      state.loginModalItem = null;
    },
    openLoginTooltip(state) {
      state.loginTooltipOpen = true;
      state.loginModalItem = null;
      state.loginButtonOpen = false;
    },
    closeLoginTooltip(state) {
      state.loginTooltipOpen = false;
    },
    collapseDetails(state) {
      state.detailsCollapsed = true;
    },
    toggleDetails(state) {
      state.queueCollapsed = true;
      state.devicesCollapsed = true;
      state.showLyrics = false;
      state.detailsCollapsed = !state.detailsCollapsed;
    },
    collapseLibrary(state) {
      state.libraryCollapsed = true;
    },
    openLibrary(state) {
      state.libraryCollapsed = false;
    },
    toggleLibrary(state) {
      state.libraryCollapsed = !state.libraryCollapsed;
    },
    collapseQueue(state) {
      state.queueCollapsed = true;
    },
    toggleQueue(state) {
      state.devicesCollapsed = true;
      state.showLyrics = false;
      state.queueCollapsed = !state.queueCollapsed;
    },
    collapseDevices(state) {
      state.devicesCollapsed = true;
    },
    toggleDevices(state) {
      state.detailsCollapsed = true;
      state.queueCollapsed = true;
      state.showLyrics = false;
      state.devicesCollapsed = !state.devicesCollapsed;
    },
    closeLyrics(state) {
      state.showLyrics = false;
    },
    toggleShowLyrics(state) {
      state.detailsCollapsed = true;
      state.queueCollapsed = true;
      state.devicesCollapsed = true;
      state.showLyrics = !state.showLyrics;
    },
    toggleFullscreenNowPlaying(state) {
      state.fullscreenNowPlaying = !state.fullscreenNowPlaying;
      if (state.fullscreenNowPlaying) {
        state.detailsCollapsed = true;
        state.libraryCollapsed = true;
      }
    },
    closeFullscreenNowPlaying(state) {
      state.fullscreenNowPlaying = false;
    },
    closeRightLayout(state) {
      state.queueCollapsed = true;
      state.devicesCollapsed = true;
      state.detailsCollapsed = true;
    },
    openRightLayout(state) {
      // Restore default details pane if everything is closed
      if (state.queueCollapsed && state.devicesCollapsed && state.detailsCollapsed) {
        state.detailsCollapsed = false;
      }
    },
    openMiniplayer(state) {
      state.miniplayerOpen = true;
    },
    closeMiniplayer(state) {
      state.miniplayerOpen = false;
    },
    toggleMiniplayer(state) {
      state.miniplayerOpen = !state.miniplayerOpen;
    },
  },
});

export const isRightLayoutOpen = createSelector(
  [
    (state: RootState) => state.ui.queueCollapsed,
    (state: RootState) => state.ui.devicesCollapsed,
    (state: RootState) => state.ui.detailsCollapsed,
  ],
  (queueCollapsed, devicesCollapsed, detailsCollapsed) => {
    return !queueCollapsed || !devicesCollapsed || !detailsCollapsed;
  }
);

export const getLibraryCollapsed = createSelector(
  [(state: RootState) => state.ui.libraryCollapsed],
  (libraryCollapsed) => libraryCollapsed
);

export const uiActions = {
  ...uiSlice.actions,
};

export default uiSlice.reducer;
